import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { VaSettingsContext } from "contexts/VaSettings";
import { useContext, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { OrganizationNiche } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledDialog from "components/Styled/Dialog";
import {
  OrganizationTemplateRubricSuggestion,
  Template,
  TemplateContentType,
  TemplateRubricName,
} from "features/Influencer/ContactList/schema";
import TemplateRubrics from "features/Influencer/HomePage/CreateTemplateDialog/TemplateRubrics";
import { getLatestRubricByName } from "features/Influencer/Templates/TemplatesTable/helpers";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { containsMergeTagsTemplates, hasAdjacentMergeTags } from "utils/string";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useBulkSend } from "utils/useBulkSend";
import { useGamification } from "utils/useGamification";
import { useSaveAsNewTemplate } from "utils/useSaveAsNewTemplate";

import SaveAsNewTemplateDialog from "../../SharedComponents/SaveAsNewTemplateDialog";
import TemplateBody from "../../SharedComponents/TemplateBody";
import { validateTemplateBody } from "../../SharedComponents/helpers";
import { ActionPill, FolderPill, TypePill } from "./components/HeaderControls";
import PreSaveDialog from "./components/PreSaveDialog";
import UnsavedChanges from "./components/UnsavedChanges";
import UserFirstTemplate from "./components/UserFirstTemplate";

const serializeForAutosave = (template: Template) =>
  JSON.stringify({
    name: template.name ?? "",
    subject: template.subject ?? "",
    body: template.body ?? "",
    attachment: template.attachment ?? null,
    contentType: template.contentType,
    folderId: template.folderId ?? null,
  });

interface EditTemplateDialogProps {
  isNew?: boolean;
  propsTemplateId?: string;
  propsHandleClose?: (e?: CustomEvent) => void;
  onSuccess?: () => void;
  defaultContentType?: TemplateContentType;
  defaultBody?: string;
}

export default function EditTemplateDialog({
  isNew = false,
  propsTemplateId,
  propsHandleClose,
  onSuccess,
  defaultContentType,
  defaultBody,
}: EditTemplateDialogProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  let { templateId } = useParams();
  if (propsTemplateId) {
    templateId = propsTemplateId;
    isNew = false;
  }
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bentoCategoryIdParam = (() => {
    const raw = searchParams.get("bento_category_id");
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : undefined;
  })();
  const { getToken } = useAuth();
  const { currentOrg, setProfile } = useContext(OrganizationUserContext);
  const { quickSendBrandId, setPreviewLoading, isEdited } = useContext(
    QuickSendDrawerContext,
  );
  const { fetchSettings, loadData } = useContext(VaSettingsContext);
  const { fetchNiches } = useBulkSend();
  const {
    templates,
    setTemplates,
    fetchOutreachTemplates,
    userHasNoTemplates,
    setDefaultTemplates,
    setUserHasNoTemplates,
    currentTemplateContentType,
    associatedNicheId,
    setAssociatedNicheId,
    askUserToEdit,
    setAskUserToEdit,
    onSaveAsNew,
    folders,
    fetchFolders,
  } = useContext(OutreachTemplatesContext);
  const {
    setTemplateForQuickSend,
    newlyAddedTemplateIndex,
    handleRefetchPreviews,
    setOutreachMessages,
  } = useContext(QuickSendContext);
  const { addTemplateTaskCompleted } = useGamification();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const getDefaultContentType = () => {
    if (defaultContentType) return defaultContentType;
    if (templates?.length === 0) return TemplateContentType.OUTREACH;
    return (
      currentTemplateContentType ||
      (newlyAddedTemplateIndex > 0
        ? TemplateContentType.FOLLOWUP
        : TemplateContentType.OUTREACH)
    );
  };
  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;

  // States
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Outreach" : "",
    subject: "",
    body: defaultBody ?? "",
    contentType: getDefaultContentType(),
    attachment: undefined,
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Follow-up" : "",
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
  });
  const [fetchTemplateLoading, setFetchTemplateLoading] = useState(
    Boolean(templateId),
  );
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState(false);
  const [openPresaveDialog, setOpenPresaveDialog] = useState<boolean>(false);
  const [openAskUserToEdit, setOpenAskUserToEdit] = useState<boolean>(false);
  const [openSaveAsNewDialog, setOpenSaveAsNewDialog] =
    useState<boolean>(false);
  const [saveAsNewName, setSaveAsNewName] = useState<string>("");
  const [openRubricsDialog, setOpenRubricsDialog] = useState<boolean>(false);
  const [runEvaluateSignal, setRunEvaluateSignal] = useState<number>(0);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);
  const [evaluationResult, setEvaluationResult] = useState<{
    suggestions: OrganizationTemplateRubricSuggestion[];
    highlightedBody: string | null;
  } | null>(null);
  const [openSkipAutoRunWarning, setOpenSkipAutoRunWarning] =
    useState<boolean>(false);
  const [skipAutoRunLoading, setSkipAutoRunLoading] = useState<boolean>(false);
  const [originalNichesList, setOriginalNichesList] = useState<
    OrganizationNiche[]
  >([]);
  const [restoreSignal, setRestoreSignal] = useState<number>(0);
  const [openAdjacentMergeTagWarning, setOpenAdjacentMergeTagWarning] =
    useState<boolean>(false);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const isAutosavingRef = useRef(false);

  useEffect(() => {
    if (isEditingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [isEditingName]);

  // Warns about two personalization tags in a row before running `proceed`.
  // Gated before the pre-save dialog so the warning always comes first.
  const requireNoAdjacentTags = (proceed: () => void) => {
    if (
      hasAdjacentMergeTags(editTemplate.body) ||
      hasAdjacentMergeTags(editTemplate.subject)
    ) {
      pendingSaveRef.current = proceed;
      setOpenAdjacentMergeTagWarning(true);
      return;
    }
    proceed();
  };
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  const handleCreateFolder = async () => {
    if (!currentOrg?.id || !newFolderName.trim()) {
      return;
    }
    setCreatingFolder(true);
    try {
      const { folder } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/template-folders`,
        "POST",
        {},
        { name: newFolderName.trim() },
      );
      await fetchFolders();
      setEditTemplate((prev) => ({ ...prev, folderId: folder.id }));
      setIsDirty(true);
      setNewFolderDialogOpen(false);
      setNewFolderName("");
      setAlert("Folder created", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRestoreContent = (subject: string, body: string) => {
    setEditTemplate((prev) => ({
      ...prev,
      body,
      ...(subject ? { subject } : {}),
    }));
    setRestoreSignal((s) => s + 1);
  };
  const hasMergeTag = containsMergeTagsTemplates(editTemplate.body || "");
  const isAutoRunInProgress =
    Boolean(editTemplate?.autoRunEnqueued) &&
    !getLatestRubricByName(TemplateRubricName.outreach_default, editTemplate);
  // Get a list of ALL OrganizationNiche inside originalNichesList that is not inside editTemplate?.niches
  const listOfUncheckNiches =
    editTemplate?.updateMultiNiches &&
    editTemplate?.contentType === TemplateContentType.OUTREACH
      ? originalNichesList.filter(
          (originalNiche) =>
            !editTemplate.niches?.some(
              (niche) => niche.id === originalNiche.id,
            ),
        )
      : [];

  const fetchTemplate = async () => {
    if (!currentOrg?.id || !templateId) {
      return;
    }
    setFetchTemplateLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateId}`,
      );
      setEditTemplate(res.template);
      setOriginalNichesList(res.template.niches || []);
      lastSavedSnapshotRef.current = serializeForAutosave(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchTemplateLoading(false);
    }
  };

  const updateReviewedFirstTemplate = async () => {
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/templates/reviewed-first-template`,
      "POST",
      {},
    );
    setProfile((prev) => ({ ...prev, reviewedFirstTemplate: true }));
  };

  const createNewForNiche = async () => {
    if (!associatedNicheId) return;
    trackEvent("Create New Template for Niche", {
      "Template Id": templateId,
      "Niche Id": associatedNicheId,
    });
    setIsDirty(false);
    setCreateLoading(true);
    try {
      const { template } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          ...editTemplate,
          ...(bentoCategoryIdParam !== undefined && {
            bentoCategoryId: bentoCategoryIdParam,
          }),
          nicheId: associatedNicheId,
          oldTemplateId: editTemplate.id,
        },
      );
      addTemplateTaskCompleted(template.contentType);
      setTemplates((prev) => [...prev, template]);
      await confirmedClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const saveAsNew = useSaveAsNewTemplate();

  const openSaveAsNewPrompt = () => {
    const { isValidTemplate, errorMessage } =
      validateTemplateBody(editTemplate);
    if (!isValidTemplate && errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }
    setSaveAsNewName(`${editTemplate.name} - Copy`);
    setOpenSaveAsNewDialog(true);
  };

  const confirmSaveAsNew = async () => {
    setCreateLoading(true);
    try {
      const newTemplate = await saveAsNew(
        editTemplate,
        saveAsNewName,
        bentoCategoryIdParam !== undefined
          ? { bentoCategoryId: bentoCategoryIdParam }
          : undefined,
      );
      if (!newTemplate) return;
      if (newTemplate.contentType === TemplateContentType.OUTREACH) {
        addTemplateTaskCompleted();
      }
      onSaveAsNew?.(newTemplate);
      setOpenSaveAsNewDialog(false);
      setAlert("Template saved as new", "success");
      await confirmedClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateOrSave = () => {
    const { isValidTemplate, errorMessage } =
      validateTemplateBody(editTemplate);
    if (!isValidTemplate && errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }

    if (
      templates.find(
        (t: Template) =>
          t.name === editTemplate.name &&
          t.contentType === editTemplate.contentType &&
          t.id !== editTemplate.id,
      )
    ) {
      setAlert("You already have a template with the same name", "error");
      return;
    }

    if (askUserToEdit && !isDirty) {
      setOpenAskUserToEdit(true);
      return;
    }

    requireNoAdjacentTags(() => {
      if (
        (isDirty &&
          (isEdited ||
            !hasMergeTag ||
            editTemplate?.attachedScheduledEmailsCount ||
            (editTemplate?.attachedNichesCount && associatedNicheId))) ||
        listOfUncheckNiches.length > 0
      ) {
        setOpenPresaveDialog(true);
      } else {
        saveTemplate();
      }
    });
  };

  const saveFromOnboarding = async () => {
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/from-onboarding`,
        "POST",
        {},
        {
          outreach: editTemplate,
          followup: followupTemplate,
        },
      );
      await fetchOutreachTemplates();
      if (outreach) {
        setEditTemplate(outreach);
      }
      if (followup) {
        setFollowupTemplate(followup);
      }
      if (outreach && outreach?.id) {
        addTemplateTaskCompleted(TemplateContentType.OUTREACH);
      }
      if (followup && followup?.id) {
        addTemplateTaskCompleted(TemplateContentType.FOLLOWUP);
      }
      // Keep the dialog open while TemplateRubrics polls for the background
      // auto-evaluation result so the user can see their score. Only close
      // immediately if no auto-run was enqueued.
      if (!outreach?.autoRunEnqueued) {
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const saveTemplate = async (updatePendingEmails?: boolean) => {
    if (!currentOrg?.id) return;
    setIsDirty(false);
    setCreateLoading(true);
    if (isUserFirstTemplate) {
      await saveFromOnboarding();
      return;
    }
    const refreshNiches = editTemplate?.updateMultiNiches;
    try {
      const isUpdating = Boolean(
        templateId || (editTemplate?.id && editTemplate?.id > 0),
      );
      const res = await fetcherAuth(
        getToken,
        isUpdating
          ? `/api/organization/${currentOrg?.id}/templates/${
              templateId || editTemplate?.id
            }`
          : `/api/organization/${currentOrg?.id}/templates`,
        isUpdating ? "PUT" : "POST",
        {},
        {
          ...editTemplate,
          ...(bentoCategoryIdParam !== undefined && {
            bentoCategoryId: bentoCategoryIdParam,
          }),
          updatePendingEmails,
          validateBulkSendVariables: isPage(routes.onboardingVaSelfServe),
        },
      );
      setAlert("Template Saved", "success");
      setEditTemplate(res.template);
      lastSavedSnapshotRef.current = serializeForAutosave(res.template);
      setAutosaveStatus("idle");
      if (userHasNoTemplates) {
        setDefaultTemplates([res.template]);
        setUserHasNoTemplates(false);
      }
      // User add new template from QuickSend mode
      if (newlyAddedTemplateIndex > -1) {
        setTemplateForQuickSend((prev) => {
          const copy = makeDeepCopy(prev);
          copy[newlyAddedTemplateIndex] = res.template;
          handleRefetchPreviews(copy);
          return copy;
        });
      }
      if (isEdited && updatePendingEmails) {
        handleUpdateQuickSend(res.template);
      }
      // Keep the dialog open so TemplateRubrics can poll for the background
      // auto-evaluation result. The user can wait for the score, or hit X to
      // confirm skipping (which deletes the template).
      await fetchOutreachTemplates();
      addTemplateTaskCompleted(res.template.contentType);
      if (!res.template.autoRunEnqueued) {
        await confirmedClose(undefined, refreshNiches);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const autosaveTemplate = async (snapshot: string) => {
    if (!currentOrg?.id || isAutosavingRef.current) return;
    const id = templateId || editTemplate?.id;
    if (!id || snapshot === lastSavedSnapshotRef.current) return;
    isAutosavingRef.current = true;
    setAutosaveStatus("saving");
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/templates/${id}`,
        "PUT",
        {},
        {
          name: editTemplate.name,
          subject: editTemplate.subject,
          body: editTemplate.body,
          attachment: editTemplate.attachment,
          contentType: editTemplate.contentType,
          ...(editTemplate.folderId !== undefined && {
            folderId: editTemplate.folderId,
          }),
          ...(bentoCategoryIdParam !== undefined && {
            bentoCategoryId: bentoCategoryIdParam,
          }),
        },
      );
      lastSavedSnapshotRef.current = snapshot;
      setAutosaveStatus("saved");
    } catch {
      setAutosaveStatus("idle");
    } finally {
      isAutosavingRef.current = false;
    }
  };

  const autosaveSnapshot = serializeForAutosave(editTemplate);

  // True when autosave has already persisted the current content and there are
  // no niche changes (which autosave doesn't cover) still waiting on a manual save.
  const isFullyAutosaved =
    !isAutosavingRef.current &&
    autosaveStatus !== "saving" &&
    lastSavedSnapshotRef.current === autosaveSnapshot &&
    !editTemplate?.updateMultiNiches;

  useEffect(() => {
    if (fetchTemplateLoading || isUserFirstTemplate || createLoading) return;
    const id = templateId || (editTemplate?.id && editTemplate.id > 0);
    if (!id) return;
    if (lastSavedSnapshotRef.current === null) {
      lastSavedSnapshotRef.current = autosaveSnapshot;
      return;
    }
    if (!isDirty || autosaveSnapshot === lastSavedSnapshotRef.current) {
      setAutosaveStatus((s) => (s === "saving" ? "saved" : s));
      return;
    }
    setAutosaveStatus("saving");
    const timer = setTimeout(() => {
      autosaveTemplate(autosaveSnapshot);
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveSnapshot, isDirty, fetchTemplateLoading, createLoading]);

  const handleUpdateQuickSend = (template: Template) => {
    trackEvent("Update QuickSend Template Button Clicked", {
      "Template Id": templateId,
    });
    setTemplateForQuickSend((prev) => {
      let foundTemplate = false;
      const copy = prev.map((t) => {
        if (t.id === template.id) {
          foundTemplate = true;
          return template;
        }
        return t;
      });
      if (foundTemplate) {
        setPreviewLoading(quickSendBrandId);
        setOutreachMessages([]);
        handleRefetchPreviews(copy);
      }
      return copy;
    });
  };

  const handleClose = (e?: CustomEvent) => {
    if (isAutoRunInProgress) {
      setOpenSkipAutoRunWarning(true);
      return;
    }
    if (isDirty && !isFullyAutosaved) {
      setOpenIsDirtyWarning(true);
    } else {
      confirmedClose(e);
    }
  };

  const confirmSkipAutoRun = async () => {
    if (!currentOrg?.id) return;
    setSkipAutoRunLoading(true);
    trackEvent("Template Rubrics - Skip Scoring");
    try {
      await fetchOutreachTemplates();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSkipAutoRunLoading(false);
      setOpenSkipAutoRunWarning(false);
    }
    if (propsHandleClose) {
      propsHandleClose();
    } else {
      navigate(`/${routes.templates}`);
    }
  };

  const confirmedClose = async (e?: CustomEvent, refreshNiches?: boolean) => {
    if (openPresaveDialog && isPage(routes.virtualAssistant)) {
      await fetchSettings();
      loadData();
    }
    if (
      refreshNiches ||
      (openPresaveDialog && isPage(routes.onboardingVaSelfServe))
    ) {
      await fetchNiches();
    }
    if (askUserToEdit && !isDirty && !openAskUserToEdit) {
      setOpenAskUserToEdit(true);
      return;
    }
    setIsDirty(false);
    setOpenIsDirtyWarning(false);
    setOpenPresaveDialog(false);
    setAssociatedNicheId(null);
    setAskUserToEdit(false);
    if (propsHandleClose) {
      propsHandleClose(e);
    } else {
      navigate(`/${routes.templates}`);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchTemplate();
      updateReviewedFirstTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id && folders.length === 0) {
      fetchFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const getKicker = () => {
    if (isUserFirstTemplate) return null;
    if (isNew) return "Create a new template";
    return "Modify your template";
  };

  const canChangeType =
    !isUserFirstTemplate && Boolean(editTemplate?.id) && templates?.length > 0;

  const showHeaderControls = !isUserFirstTemplate && !fetchTemplateLoading;
  const showInlineMobileClose = !isUserFirstTemplate && !isEditingName;
  const kicker = getKicker();

  const dialogTitle = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        minWidth: 0,
        position: "relative",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 4, md: 0 } }}>
        {isUserFirstTemplate ? (
          <Box
            sx={{
              fontSize: { xs: 20, md: 24 },
              color: "black",
              lineHeight: 1.2,
            }}
          >
            Create your first pitch
          </Box>
        ) : (
          <>
            {kicker && (
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: 11,
                  fontWeight: 400,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {kicker}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: { xs: "100%", md: "auto" },
              }}
            >
              {isEditingName ? (
                <Box
                  component="input"
                  ref={nameInputRef}
                  value={editTemplate.name ?? ""}
                  placeholder="Template name"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEditTemplate((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }));
                    setIsDirty(true);
                  }}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === "Escape") {
                      setIsEditingName(false);
                    }
                  }}
                  sx={{
                    fontSize: { xs: 20, md: 24 },
                    color: "text.primary",
                    fontFamily: "inherit",
                    lineHeight: 1.2,
                    border: "none",
                    outline: "none",
                    bgcolor: "transparent",
                    borderBottom: `1px solid ${grey[300]}`,
                    padding: "2px 0",
                    "&:focus": { borderColor: "primary.main" },
                  }}
                />
              ) : (
                <>
                  <Box
                    sx={{
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {editTemplate.name || "Untitled template"}
                  </Box>
                  <IconButton
                    sx={{ fontSize: 11 }}
                    onClick={() => setIsEditingName(true)}
                  >
                    <Box component="i" className="fa-regular fa-pen" />
                  </IconButton>
                </>
              )}
            </Box>
          </>
        )}
        {showHeaderControls && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 0.75,
              mt: 1,
            }}
          >
            {canChangeType && (
              <TypePill
                compact
                value={editTemplate.contentType}
                onChange={(value) => {
                  trackEvent(
                    "Create Template Dialog Form - Select Template Type",
                    { "Template Type": value },
                  );
                  setEditTemplate((prev) => ({ ...prev, contentType: value }));
                  setIsDirty(true);
                }}
              />
            )}
            <FolderPill
              compact
              folderId={editTemplate.folderId ?? null}
              folders={folders}
              onChange={(id) => {
                setEditTemplate((prev) => ({ ...prev, folderId: id }));
                setIsDirty(true);
              }}
              onNewFolder={() => {
                setNewFolderName("");
                setNewFolderDialogOpen(true);
              }}
            />
            <ActionPill
              compact
              icon="fa-regular fa-wand-magic-sparkles"
              label="Evaluate"
              onClick={() => setOpenRubricsDialog(true)}
            />
          </Box>
        )}
      </Box>
      {showInlineMobileClose && (
        <IconButton
          onClick={handleClose}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            position: "absolute",
            top: 0,
            right: 0,
            height: 28,
            width: 28,
            padding: 0,
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={{ color: grey[400], fontSize: 16 }}
          />
        </IconButton>
      )}
    </Box>
  );

  const rightAction = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        justifyContent: "flex-end",
      }}
    >
      {showHeaderControls && canChangeType && (
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <TypePill
            value={editTemplate.contentType}
            onChange={(value) => {
              trackEvent("Create Template Dialog Form - Select Template Type", {
                "Template Type": value,
              });
              setEditTemplate((prev) => ({ ...prev, contentType: value }));
              setIsDirty(true);
            }}
          />
        </Box>
      )}
      {showHeaderControls && (
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <FolderPill
            folderId={editTemplate.folderId ?? null}
            folders={folders}
            onChange={(id) => {
              setEditTemplate((prev) => ({ ...prev, folderId: id }));
              setIsDirty(true);
            }}
            onNewFolder={() => {
              setNewFolderName("");
              setNewFolderDialogOpen(true);
            }}
          />
        </Box>
      )}
      <IconButton
        onClick={handleClose}
        sx={{
          display: showInlineMobileClose
            ? { xs: "none", md: "inline-flex" }
            : "inline-flex",
          height: 28,
          width: 28,
        }}
      >
        <Box
          component="i"
          className="fa-regular fa-xmark"
          sx={{ color: grey[400], fontSize: 14 }}
        />
      </IconButton>
    </Box>
  );

  return (
    <StyledDialog
      title={dialogTitle}
      rightAction={rightAction}
      open={true}
      maxWidth="lg"
      handleClose={handleClose}
      actionContainer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          {autosaveStatus === "idle" ? (
            <Box />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: autosaveStatus === "saving" ? "#E5A50A" : "#3BA55D",
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 13 }}
              >
                {autosaveStatus === "saving"
                  ? "Saving your updates…"
                  : "Latest changes saved."}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              disableElevation
              variant="text"
              onClick={handleClose}
              sx={{
                color: "text.primary",
                fontSize: 13,
                fontWeight: 500,
                textTransform: "none",
                borderRadius: 999,
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: "grey.100" },
              }}
            >
              <Box
                component="i"
                className="fa-regular fa-arrow-left"
                sx={{ mr: 1, fontSize: 13 }}
              />
              Back
            </Button>
            {Boolean(
              templateId || (editTemplate?.id && editTemplate.id > 0),
            ) && (
              <LoadingButton
                loading={createLoading}
                disableElevation
                variant="outlined"
                onClick={openSaveAsNewPrompt}
                sx={{
                  borderRadius: 8,
                  px: 2.5,
                  py: 0.875,
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: "none",
                  borderColor: "grey.300",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "grey.400",
                    backgroundColor: "grey.50",
                  },
                }}
              >
                <Box
                  component="i"
                  className="fa-regular fa-copy"
                  sx={{ mr: 1, fontSize: 13 }}
                />
                {isMobileScreen ? "Save as New" : "Save as new template"}
              </LoadingButton>
            )}
            <LoadingButton
              loading={createLoading}
              disableElevation
              variant="contained"
              onClick={() => handleCreateOrSave()}
              sx={{
                borderRadius: 8,
                px: 3.25,
                py: 0.875,
                fontSize: 13,
                letterSpacing: "0.04em",
                textTransform: "none",
              }}
            >
              {isMobileScreen ? "Save" : "Save Template"}
            </LoadingButton>
          </Box>
        </Box>
      }
    >
      {isUserFirstTemplate ? (
        <UserFirstTemplate
          createLoading={createLoading}
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          followupTemplate={followupTemplate}
          setFollowupTemplate={setFollowupTemplate}
          setIsDirty={setIsDirty}
        />
      ) : (
        <>
          {fetchTemplateLoading ? (
            <Grid sx={{ width: { xs: 350, md: 800 } }}>
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton
                animation="wave"
                width="100%"
                height="400px"
                sx={{ mt: -10, mb: -8 }}
              />
              <Skeleton animation="wave" width="100%" height="80px" />
            </Grid>
          ) : (
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Dialog
                  open={newFolderDialogOpen}
                  onClose={() => setNewFolderDialogOpen(false)}
                >
                  <DialogTitle>New folder</DialogTitle>
                  <DialogContent sx={{ minWidth: 360 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateFolder();
                      }}
                      sx={{ mt: 1 }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setNewFolderDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      disabled={creatingFolder || !newFolderName.trim()}
                      onClick={handleCreateFolder}
                    >
                      Create
                    </Button>
                  </DialogActions>
                </Dialog>
                <TemplateBody
                  editTemplate={editTemplate}
                  setEditTemplate={setEditTemplate}
                  setIsDirty={setIsDirty}
                  isDirty={isDirty}
                  isNew={isNew}
                  allowChangeType={Boolean(editTemplate?.id)}
                  allowChangeTitle={false}
                  restoreSignal={restoreSignal}
                  onEvaluate={() => setRunEvaluateSignal((s) => s + 1)}
                  evaluating={isEvaluating}
                  evaluationResult={evaluationResult}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md
                sx={{ display: { xs: "none", md: "block" } }}
              >
                <TemplateRubrics
                  template={editTemplate}
                  setTemplate={setEditTemplate}
                  onRestoreContent={handleRestoreContent}
                  runEvaluateSignal={runEvaluateSignal}
                  onLoadingChange={setIsEvaluating}
                  onEvaluated={setEvaluationResult}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}

      <PreSaveDialog
        open={openPresaveDialog}
        handleClose={() => setOpenPresaveDialog(false)}
        template={editTemplate}
        handleConfirm={saveTemplate}
        loading={createLoading}
        createNewForNiche={createNewForNiche}
        listOfUncheckNiches={listOfUncheckNiches}
      />

      <UnsavedChanges
        isUserFirstTemplate={isUserFirstTemplate}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />

      <ConfirmDialog
        header="Save template without editing?"
        subheader="You haven't made any changes to the AI-generated template. Would you like to continue editing or save it as is?"
        open={openAskUserToEdit}
        handleClose={() => setOpenAskUserToEdit(false)}
        handleCancel={() => {
          setOpenAskUserToEdit(false);
          trackEvent("Create Template Dialog Form - Continue Editing", {
            route: location.pathname,
          });
        }}
        cancelText="Continue Editing"
        confirmText="Save As Is"
        handleConfirm={() => {
          trackEvent("Create Template Dialog Form - Save As Is", {
            route: location.pathname,
          });
          setOpenAskUserToEdit(false);
          setAskUserToEdit(false);
          requireNoAdjacentTags(() => saveTemplate());
        }}
      />

      <ConfirmDialog
        header="Two personalization tags in a row"
        subheader={
          <>
            We detected two personalization tags in a row in your template (e.g.{" "}
            <Box
              component="span"
              sx={{ color: "grey.400", whiteSpace: "nowrap" }}
            >
              {"{First Name}"}
            </Box>{" "}
            <Box
              component="span"
              sx={{ color: "grey.400", whiteSpace: "nowrap" }}
            >
              {"{Brand Name}"}
            </Box>
            ). Do you want to save it anyway?
          </>
        }
        open={openAdjacentMergeTagWarning}
        handleClose={() => setOpenAdjacentMergeTagWarning(false)}
        handleCancel={() => setOpenAdjacentMergeTagWarning(false)}
        cancelText="Keep editing"
        confirmText="Save anyway"
        handleConfirm={() => {
          setOpenAdjacentMergeTagWarning(false);
          const proceed = pendingSaveRef.current;
          pendingSaveRef.current = null;
          proceed?.();
        }}
      />

      <ConfirmDialog
        header="Are you sure you want to skip seeing your template score?"
        subheader="Your template is still being evaluated. Closing now will discard this template — you can always create a new one later."
        open={openSkipAutoRunWarning}
        handleClose={() => setOpenSkipAutoRunWarning(false)}
        handleCancel={() => setOpenSkipAutoRunWarning(false)}
        cancelText="Wait for my score"
        confirmText="Skip & discard"
        handleConfirm={confirmSkipAutoRun}
        loading={skipAutoRunLoading}
      />

      <Dialog
        open={openRubricsDialog}
        onClose={() => setOpenRubricsDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobileScreen}
      >
        <DialogTitle>Evaluate my template</DialogTitle>
        <DialogContent>
          <TemplateRubrics
            template={editTemplate}
            setTemplate={setEditTemplate}
            onRestoreContent={handleRestoreContent}
            onLoadingChange={setIsEvaluating}
            onEvaluated={setEvaluationResult}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenRubricsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <SaveAsNewTemplateDialog
        open={openSaveAsNewDialog}
        name={saveAsNewName}
        loading={createLoading}
        onNameChange={setSaveAsNewName}
        onClose={() => setOpenSaveAsNewDialog(false)}
        onConfirm={confirmSaveAsNew}
      />
    </StyledDialog>
  );
}
